// src/components/AlertBanner.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  FaTools,
  FaExclamationTriangle,
  FaServer,
  FaCheckCircle,
  FaInfoCircle,
  FaTimes,
} from "react-icons/fa";
import "./AlertBanner.css";

const TYPE_MAP = {
  maintenance: {
    icon: <FaTools aria-hidden />,
    className: "alert-maintenance",
    ariaRole: "status",
  },
  down: {
    icon: <FaServer aria-hidden />,
    className: "alert-down",
    ariaRole: "alert",
  },
  warning: {
    icon: <FaExclamationTriangle aria-hidden />,
    className: "alert-warning",
    ariaRole: "alert",
  },
  info: {
    icon: <FaInfoCircle aria-hidden />,
    className: "alert-info",
    ariaRole: "status",
  },
  success: {
    icon: <FaCheckCircle aria-hidden />,
    className: "alert-success",
    ariaRole: "status",
  },
  error: {
    icon: <FaExclamationTriangle aria-hidden />,
    className: "alert-error",
    ariaRole: "alert",
  },
};

function inWindow(start, end, now = new Date()) {
  const s = start ? new Date(start) : null;
  const e = end ? new Date(end) : null;
  if (s && now < s) return false;
  if (e && now > e) return false;
  return true;
}

/**
 * Props:
 * - id: string (used to remember dismiss state)
 * - type: 'maintenance' | 'down' | 'warning' | 'info' | 'success' | 'error'
 * - title?: string
 * - message: string | ReactNode
 * - start?: ISO string/date  (optional maintenance window)
 * - end?: ISO string/date
 * - sticky?: boolean (fixed at top)
 * - dismissible?: boolean
 * - onClose?: () => void
 */
export default function AlertBanner({
  id = "global-alert",
  type = "info",
  title,
  message,
  start,
  end,
  sticky = true,
  dismissible = true,
  onClose,
}) {
  const [hidden, setHidden] = useState(false);

  // Respect prior dismissal for this specific event id
  useEffect(() => {
    const key = `alert:dismissed:${id}`;
    const val = localStorage.getItem(key);
    setHidden(val === "1");
  }, [id]);

  const active = useMemo(() => inWindow(start, end), [start, end]);

  if (hidden || !active) return null;

  const conf = TYPE_MAP[type] ?? TYPE_MAP.info;

  const handleClose = () => {
    localStorage.setItem(`alert:dismissed:${id}`, "1");
    setHidden(true);
    onClose?.();
  };

  return (
    <div
      className={[
        "alert-banner",
        conf.className,
        sticky ? "is-sticky" : "",
      ].join(" ")}
      role={conf.ariaRole}
      aria-live={conf.ariaRole === "alert" ? "assertive" : "polite"}
    >
      <div className="alert-inner">
        <div className="alert-icon">{conf.icon}</div>
        <div className="alert-text">
          {title && <div className="alert-title">{title}</div>}
          <div className="alert-message">{message}</div>
          {start && end && (
            <div className="alert-window">
              {new Date(start).toLocaleString()} — {new Date(end).toLocaleString()}
            </div>
          )}
        </div>
        {dismissible && (
          <button className="alert-dismiss" onClick={handleClose} aria-label="Dismiss alert">
            <FaTimes />
          </button>
        )}
      </div>
    </div>
  );
}
